<template src="./contact.html"></template>

<script>
  import loader from '../loader/spinner/spinner'
  import uploader from '../common/fileupload/fileupload'
  import axios from 'axios'

  const ALPHA = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z','#']

  export default {
    name: 'contact',
    components: {
      'app-loader': loader,
      'app-uploader': uploader
    },
    computed: {
      xeroImportAvailable () {
        const integ = this.$store && this.$store.getters['company/getIntegrations']
        return !!(integ && integ.xero === true)
      },
      sm8ImportAvailable () {
        const integ = this.$store && this.$store.getters['company/getIntegrations']
        return !!(integ && integ.service_m8 === true)
      },
      displayContacts () {
        if (this.searchText !== '' && this.searchResults.length > 0) return this.searchResults
        if (this.searchText !== '' && this.searchResults.length === 0) return []
        return this.contacts
      },
      groupedContacts () {
        const groups = {}
        this.displayContacts.forEach(c => {
          const name = c.company || ((c.first_name || '') + ' ' + (c.last_name || ''))
          const ch = String(name).trim().charAt(0).toUpperCase()
          const key = /^[A-Z]$/.test(ch) ? ch : '#'
          if (!groups[key]) groups[key] = []
          groups[key].push(c)
        })
        return groups
      },
      sectionKeys () {
        return Object.keys(this.groupedContacts).sort((a, b) => {
          if (a === '#') return 1
          if (b === '#') return -1
          return a.localeCompare(b)
        })
      },
      sectionKeySet () {
        const set = {}
        this.sectionKeys.forEach(k => { set[k] = true })
        return set
      },
      railItems () {
        const slots = Math.max(3, this.railSlots)
        if (slots >= ALPHA.length) {
          return ALPHA.map(l => ({ type: 'letter', value: l }))
        }
        // Each pair of letters separated by one spacer: letters L → total = 2L - 1
        const numLetters = Math.max(2, Math.min(ALPHA.length, Math.floor((slots + 1) / 2)))
        const indices = []
        for (let i = 0; i < numLetters; i++) {
          const idx = Math.round(i * (ALPHA.length - 1) / (numLetters - 1))
          if (indices.indexOf(idx) === -1) indices.push(idx)
        }
        if (indices[0] !== 0) indices.unshift(0)
        if (indices[indices.length - 1] !== ALPHA.length - 1) indices.push(ALPHA.length - 1)
        const out = []
        for (let i = 0; i < indices.length; i++) {
          out.push({ type: 'letter', value: ALPHA[indices[i]] })
          if (i < indices.length - 1 && indices[i + 1] - indices[i] > 1) {
            out.push({ type: 'spacer', value: '\u2022' })
          }
        }
        return out
      }
    },
    data: function () {
      return {
        contacts: [],
        uploadVisible: false,
        searchText: '',
        searchResults: [],
        loading: false,
        xeroImporting: false,
        sm8Importing: false,
        railSlots: ALPHA.length,
        railDragging: false,
        activeRailLetter: '',
        railBubbleTop: 0
      }
    },
    methods: {
      getContacts: function () {
        this.loading = true
        axios.get('/api/contact?page=1&limit=5000').then(response => {
          if (response.data.contacts) {
            this.contacts = response.data.contacts
          } else if (Array.isArray(response.data)) {
            this.contacts = response.data
          } else {
            this.contacts = []
          }
          this.loading = false
          this.$nextTick(() => this.recomputeRailSlots())
        }).catch((exception) => {
          console.error('Error fetching contacts:', exception)
          this.loading = false
          if (exception.response && exception.response.status === 401) {
            this.$router.push('/login')
          }
        })
      },

      newContact: function () {
        this.$router.push('/customer/new')
      },
      goToCustomer: function (contact) {
        this.$router.push('/customer/' + contact.id)
      },

      search: function () {
        if (this.searchText && this.searchText.length >= 2) {
          axios.post('/api/contact/fetch/search', { text: this.searchText }).then(response => {
            if (response.data.contacts) {
              this.searchResults = response.data.contacts
            } else if (Array.isArray(response.data)) {
              this.searchResults = response.data
            } else {
              this.searchResults = []
            }
            this.$nextTick(() => this.recomputeRailSlots())
          }).catch(() => {
            this.searchResults = []
          })
        } else {
          this.searchResults = []
          this.$nextTick(() => this.recomputeRailSlots())
        }
      },

      importXero () {
        if (this.xeroImporting) return
        this.xeroImporting = true
        axios.post('/api/contact/import/xero').then((response) => {
          const p = response.data && response.data.payload ? response.data.payload : {}
          let msg = `Xero import complete — ${p.created || 0} created, ${p.updated || 0} updated`
          if (p.pages) msg += ` (${p.pages} page${p.pages === 1 ? '' : 's'})`
          this.successNotification(msg)
          this.getContacts()
        }).catch((error) => {
          const msg = error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'Failed to import contacts from Xero'
          this.errorNotification(msg)
        }).finally(() => {
          this.xeroImporting = false
        })
      },

      loadIntegrations () {
        const profile = this.$store && this.$store.getters['user/getProfile']
        const companyId = profile && profile.company_id
        if (!companyId) return
        axios.get('/api/company/' + companyId).then((response) => {
          if (response.data && response.data.integrations) {
            this.$store.commit('company/setIntegrations', response.data.integrations)
          }
        }).catch(() => { /* non-fatal */ })
      },

      importSm8 () {
        if (this.sm8Importing) return
        this.sm8Importing = true
        axios.post('/api/integrations/servicem8/sync/contacts').then((response) => {
          const { created = 0, updated = 0 } = response.data
          this.successNotification(
            `ServiceM8 import complete — ${created} new customer${created !== 1 ? 's' : ''} added, ${updated} linked`
          )
          this.getContacts()
        }).catch((error) => {
          const msg = error && error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : 'Failed to import contacts from ServiceM8'
          this.errorNotification(msg)
        }).finally(() => {
          this.sm8Importing = false
        })
      },

      formatCell: function (value) {
        if (value == null || String(value).trim() === '') return ''
        const s = String(value).trim()
        if (s.indexOf('{{') !== -1 && s.indexOf('}}') !== -1) return ''
        return s
      },
      isCellEmpty: function (value) {
        return this.formatCell(value) === ''
      },
      exportCsv: function () {
        axios({ url: '/api/contact/export/csv', method: 'GET', responseType: 'blob' })
          .then(response => {
            const url  = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href  = url
            link.setAttribute('download', 'contacts.csv')
            document.body.appendChild(link)
            link.click()
          }).catch((exception) => {
            console.error('Error exporting contacts:', exception)
          })
      },

      // ── Letter rail ───────────────────────────────────────────────
      recomputeRailSlots () {
        // Available vertical space for the rail (capped at 80vh via CSS)
        const maxRailHeight = Math.min(window.innerHeight * 0.8, window.innerHeight - 120)
        const itemHeight = 16 // approx px per rail item (letter or spacer)
        const slots = Math.max(3, Math.floor(maxRailHeight / itemHeight))
        this.railSlots = Math.min(ALPHA.length, slots)
      },
      scrollToSection (letter, behavior) {
        if (!this.sectionKeySet[letter]) return
        const el = document.getElementById('contacts-section-' + letter)
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: behavior || 'smooth', block: 'start' })
        }
      },
      // ── Drag-to-scroll (iOS Contacts style) ───────────────────────
      alphaIndexOf (key) {
        return ALPHA.indexOf(key)
      },
      nearestSectionKey (idx) {
        let best = null
        let bestDist = Infinity
        this.sectionKeys.forEach((k) => {
          const d = Math.abs(this.alphaIndexOf(k) - idx)
          if (d < bestDist) {
            bestDist = d
            best = k
          }
        })
        return best
      },
      updateRailFromPointer (clientY) {
        const rail = this.$refs.letterRail
        if (!rail) return
        const rect = rail.getBoundingClientRect()
        let ratio = (clientY - rect.top) / rect.height
        ratio = Math.max(0, Math.min(1, ratio))
        const idx = Math.round(ratio * (ALPHA.length - 1))
        const key = this.nearestSectionKey(idx)
        this.railBubbleTop = Math.max(rect.top, Math.min(rect.bottom, clientY))
        if (key) {
          this.activeRailLetter = key
          if (key !== this._lastRailKey) {
            this._lastRailKey = key
            // Instant follow while dragging; smooth would lag behind the finger.
            this.scrollToSection(key, 'auto')
          }
        }
      },
      onRailPointerDown (e) {
        this.railDragging = true
        this._lastRailKey = null
        const rail = this.$refs.letterRail
        if (rail && rail.setPointerCapture) {
          try { rail.setPointerCapture(e.pointerId) } catch (_) { /* ignore */ }
        }
        this.updateRailFromPointer(e.clientY)
      },
      onRailPointerMove (e) {
        if (!this.railDragging) return
        this.updateRailFromPointer(e.clientY)
      },
      onRailPointerEnd (e) {
        if (!this.railDragging) return
        this.railDragging = false
        this.activeRailLetter = ''
        const rail = this.$refs.letterRail
        if (rail && rail.releasePointerCapture && e && e.pointerId != null) {
          try { rail.releasePointerCapture(e.pointerId) } catch (_) { /* ignore */ }
        }
      },
      onResize () {
        this.recomputeRailSlots()
      }
    },
    created: function () {
      if (localStorage.getItem('token')) {
        this.getContacts()
        this.loadIntegrations()
      } else {
        this.$router.push('/login')
      }
    },
    mounted () {
      this.recomputeRailSlots()
      window.addEventListener('resize', this.onResize)
    },
    beforeUnmount () {
      window.removeEventListener('resize', this.onResize)
    }
  }
</script>

<style scoped>
/* ── Page header ────────────────────────────────────────────────── */
.customers-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.customers-page-header h1 {
  margin: 0;
}

.customers-page-header .ui.primary.button {
  margin-right: 0;
}

/* ── Search ─────────────────────────────────────────────────────── */
.customers-search-row {
  margin-bottom: 12px;
}

.customers-search-row .smardie-text-input input {
  height: 44px !important;
  line-height: 44px !important;
}

/* ── Import action buttons ──────────────────────────────────────── */
.customers-import-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.ui.customers-import-btn.button {
  display: inline-flex !important;
  align-items: center;
  gap: 8px;
  background: var(--color-bg-button-secondary) !important;
  border: 1px solid var(--color-border) !important;
  border-radius: 6px !important;
  color: var(--color-text) !important;
  font-weight: 500 !important;
  height: 40px;
  padding: 0 16px !important;
  cursor: pointer;
  box-shadow: none !important;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.ui.customers-import-btn.button:hover {
  border-color: var(--color-text) !important;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08) !important;
}

.ui.customers-import-btn.button.loading {
  opacity: 0.7;
  pointer-events: none;
}

.import-btn-logo {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex-shrink: 0;
}

/* ── Section headers ────────────────────────────────────────────── */
.customers-grid-wrapper {
  position: relative;
}

.customers-grid-wrapper.ui.grid {
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.customers-grid-wrapper.ui.grid > .column {
  padding-left: 0 !important;
  padding-right: 16px !important;
}

.customers-grid-wrapper .ui.bottom.attached.tab.segment {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.customers-contact-table.ui.table thead,
.customers-contact-table.ui.table:not(.unstackable) thead {
  display: none !important;
}

.customers-contact-table.ui.table,
.customers-contact-table.ui.table th,
.customers-contact-table.ui.table td,
.customers-contact-table.ui.table thead th {
  border-color: var(--color-border-table) !important;
}

.customers-contact-table.ui.table thead th {
  border-bottom: 1px solid var(--color-border-table) !important;
}

.contacts-section-row,
.contacts-section-row td.contacts-section-header {
  background: var(--color-bg-elevated) !important;
}

.contacts-section-row td.contacts-section-header {
  color: var(--color-text-muted) !important;
  font-weight: 600 !important;
  font-size: 0.85em;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 6px 12px !important;
  cursor: default;
}

[data-theme="dark"] .contacts-section-row,
[data-theme="dark"] .contacts-section-row td.contacts-section-header {
  background: var(--color-near-black) !important;
}

[data-theme="dark"] .contacts-section-row td.contacts-section-header {
  color: var(--color-white) !important;
}

.contacts-section-row {
  cursor: default !important;
}

/* No visible row hover — match resting surface (same idea as dark mode) */
.customers-contact-table.ui.selectable.table tbody tr:hover,
.customers-contact-table.ui.selectable.table tbody tr:hover > td {
  background: var(--color-bg-surface) !important;
}

.customers-contact-table.ui.selectable.table tbody tr.contacts-section-row:hover,
.customers-contact-table.ui.selectable.table tbody tr.contacts-section-row:hover > td,
.customers-contact-table.ui.selectable.table tbody tr.contacts-section-row:hover > td.contacts-section-header {
  background: var(--color-bg-elevated) !important;
}

[data-theme="dark"] .customers-contact-table.ui.selectable.table tbody tr.contacts-section-row:hover,
[data-theme="dark"] .customers-contact-table.ui.selectable.table tbody tr.contacts-section-row:hover > td,
[data-theme="dark"] .customers-contact-table.ui.selectable.table tbody tr.contacts-section-row:hover > td.contacts-section-header {
  background: var(--color-near-black) !important;
}

/* Contact person name + address match phone/mobile/email cell typography */
.customers-contact-table .cell-sub-description {
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}

/* ── Letter rail ────────────────────────────────────────────────── */
.contacts-letter-rail {
  position: fixed;
  right: 10px;
  top: 60%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  max-height: 80vh;
  z-index: 20;
  user-select: none;
  touch-action: none;
  padding: 6px 4px;
  background: var(--color-bg-page);
  border-radius: 10px;
}

.contacts-letter-rail .rail-item {
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  color: var(--color-border);
  padding: 0 2px;
  text-align: center;
}

.contacts-letter-rail .rail-letter {
  cursor: pointer;
}

.contacts-letter-rail .rail-letter.rail-active {
  color: #4da1ff;
}

.contacts-letter-rail .rail-letter:hover {
  color: #4da1ff;
}

.contacts-letter-rail .rail-spacer {
  font-size: 8px;
  line-height: 10px;
  color: #4da1ff;
  cursor: default;
}

/* iOS-style magnified letter bubble shown while dragging the rail */
.contacts-rail-bubble {
  position: fixed;
  right: 44px;
  transform: translateY(-50%);
  z-index: 21;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #4da1ff;
  color: #fff;
  font-size: 26px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  pointer-events: none;
}

/* ── Mobile ─────────────────────────────────────────────────────── */
@media (max-width: 767px) {
  .customers-page-header h1 {
    font-size: 1.5em;
  }
  .contacts-letter-rail {
    right: 4px;
    top: 60%;
    padding: 4px 2px;
  }

  /* Stackable table rows: centre content and drop empty phone/mobile/email cells */
  .customers-contact-table.ui.table:not(.unstackable) tbody tr.contact-row {
    box-shadow: none !important;
    border-bottom: 1px solid var(--color-border-table) !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
  }

  .customers-contact-table.ui.table:not(.unstackable) tbody tr.contact-row > td.contact-cell-empty {
    display: none !important;
  }

  .customers-contact-table.ui.table:not(.unstackable) tbody tr.contact-row > td {
    padding-top: 0.2em !important;
    padding-bottom: 0.2em !important;
  }

  .customers-contact-table.ui.table:not(.unstackable) tbody tr.contact-row > td:first-child {
    padding-top: 0.35em !important;
  }

  .customers-contact-table.ui.table:not(.unstackable) tbody tr.contact-row > td:last-child:not(.contact-cell-empty) {
    padding-bottom: 0.35em !important;
  }
}
</style>
